<template>
  <div
    class="chat-list-item d-flex align-center px-3 py-2"
    :class="{ 'chat-list-item--selected': selected }"
    @click="$emit('click')"
  >
    <!-- Avatar with Online Badge -->
    <div class="avatar-wrapper mr-3" style="position: relative; flex-shrink: 0">
      <v-avatar size="48">
        <v-img v-if="chatAvatar" :src="chatAvatar" />
        <div
          v-else
          class="d-flex align-center justify-center white--text text-h6 font-weight-medium"
          :style="`background: ${avatarColor}; width: 100%; height: 100%; border-radius: 50%`"
        >
          {{ chatInitial }}
        </div>
      </v-avatar>
      <!-- Online dot -->
      <span
        v-if="isOnline"
        class="online-dot"
      ></span>
    </div>

    <!-- Chat Info -->
    <div class="flex-grow-1 overflow-hidden">
      <div class="d-flex align-center justify-space-between mb-1">
        <span class="text-subtitle-2 font-weight-medium text-truncate" style="color: #111; max-width: 70%">
          {{ chatName }}
        </span>
        <span class="text-caption flex-shrink-0 ml-1" :class="unreadCount > 0 ? 'success--text font-weight-medium' : 'grey--text'">
          {{ lastMessageTime }}
        </span>
      </div>

      <div class="d-flex align-center justify-space-between">
        <div class="text-caption text-truncate d-flex align-center" style="color: #777; max-width: 80%">
          <!-- Own message tick -->
          <v-icon
            v-if="lastMessage && lastMessage.sender_id === user.id"
            size="15"
            :color="lastMessage.seen ? '#4fc3f7' : '#aaa'"
            class="mr-1 flex-shrink-0"
          >
            {{ lastMessage.seen ? 'mdi-check-all' : 'mdi-check' }}
          </v-icon>
          <span :class="{ 'font-weight-bold': unreadCount > 0, 'grey--text text--darken-3': unreadCount > 0 }">
            {{ lastMessageText }}
          </span>
        </div>

        <!-- Unread badge -->
        <div
          v-if="unreadCount > 0"
          class="unread-badge"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const AVATAR_COLORS = [
  '#e53935', '#d81b60', '#8e24aa', '#5e35b1',
  '#1e88e5', '#00897b', '#43a047', '#fb8c00',
  '#f4511e', '#6d4c41', '#546e7a', '#039be5',
];

export default {
  name: 'ChatListItem',

  props: {
    chat: { type: Object, required: true },
    selected: { type: Boolean, default: false }
  },

  computed: {
    ...mapGetters('chat', ['getUnreadCount', 'isUserOnline']),

    chatName() {
      if (this.chat.is_group) return this.chat.name || 'Group Chat';
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other ? `${other.first_name} ${other.last_name}`.trim() : 'Chat';
    },

    chatAvatar() {
      if (this.chat.is_group) {
        return this.chat.image_path ? this.mediaUrl + this.chat.image_path : null;
      }
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other?.image_path ? this.mediaUrl + other.image_path : null;
    },

    chatInitial() {
      if (this.chat.is_group) return (this.chat.name || 'G').charAt(0).toUpperCase();
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other?.first_name ? other.first_name.charAt(0).toUpperCase() : 'U';
    },

    avatarColor() {
      const seed = (this.chat.name || this.chatName || '').charCodeAt(0) || 0;
      return AVATAR_COLORS[seed % AVATAR_COLORS.length];
    },

    isOnline() {
      if (this.chat.is_group) return false;
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other ? this.isUserOnline(other.id) : false;
    },

    lastMessage() {
      const messages = this.$store.state.chat.messages[this.chat.id] || [];
      return messages.length > 0 ? messages[messages.length - 1] : null;
    },

    lastMessageText() {
      if (!this.lastMessage) return 'No messages yet';

      let prefix = '';
      if (this.chat.is_group && this.lastMessage.sender_id !== this.user.id) {
        const sender = this.lastMessage.sender || this.chat.users?.find(u => u.id === this.lastMessage.sender_id);
        if (sender) prefix = `${sender.first_name}: `;
      } else if (this.lastMessage.sender_id === this.user.id) {
        prefix = 'You: ';
      }

      if (this.lastMessage.file_path && !this.lastMessage.message) {
        const ext = this.lastMessage.file_path.split('.').pop().toLowerCase();
        const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        return prefix + (imgExts.includes(ext) ? '📷 Photo' : '📎 File');
      }

      return prefix + (this.lastMessage.message || '');
    },

    lastMessageTime() {
      if (!this.lastMessage) return '';
      const d = new Date(this.lastMessage.created_at);
      const now = new Date();
      const days = Math.floor((now - d) / 86400000);
      if (days === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      if (days === 1) return 'Yesterday';
      if (days < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    unreadCount() {
      return this.getUnreadCount(this.chat.id);
    }
  }
};
</script>

<style scoped>
.chat-list-item {
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
  min-height: 72px;
}

.chat-list-item:hover {
  background: #f5f5f5;
}

.chat-list-item--selected {
  background: #fffde7;
  border-left: 3px solid #43a047;
}

.chat-list-item--selected:hover {
  background: #fff9c4;
}

.online-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #4caf50;
  border: 2px solid #fff;
  border-radius: 50%;
}

.unread-badge {
  background: #43a047;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  flex-shrink: 0;
}
</style>
